import React, { useContext, useEffect, useRef, useState } from "react";
import "./YearlySnapshot.scss";
import moment from "moment";
import * as _ from "lodash-es";
import * as d3 from "d3";
import { OwIconButton } from "../../shared/OwIconButton/OwIconButton";
import OwTitle from "../../shared/OwTitle/OwTitle";
import SupplierService from "../../../services/SupplierService";
import SupplierContext from "../../../contexts/SupplierContext";

type Props = {
    orderVolume: any;
    shipmentVolume: any;
    inEditor?: boolean;
    removeWidget?: () => void;
};

interface ChartDataPoint {
    month: string;
    month_num: number;
    order_count: number;
    shipment_count: number;
}

export const YearlySnapshot = (props: Props) => {
    const chartRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const supplierModel = useContext(SupplierContext);
    const [hasData, setHasData] = useState(false);
    const chartInitialized = useRef(false);

    const getMonthLabel = (val: number): string => {
        switch (val) {
            case 1:
                return "Jan";
            case 2:
                return "Feb";
            case 3:
                return "Mar";
            case 4:
                return "Apr";
            case 5:
                return "May";
            case 6:
                return "Jun";
            case 7:
                return "Jul";
            case 8:
                return "Aug";
            case 9:
                return "Sep";
            case 10:
                return "Oct";
            case 11:
                return "Nov";
            case 12:
                return "Dec";
            default:
                return "";
        }
    };

    const getLastTwelveMonths = () => {
        const currentMoment = moment();
        const months = [{ order_count: 0, month: currentMoment.month() + 1 }];

        for (let i = 1; i < 12; ++i) {
            const pastMonth = currentMoment.clone().subtract(i, "month");
            const detail = { order_count: 0, month: pastMonth.month() + 1 };
            months.unshift(detail);
        }

        return months;
    };

    const _getOrderVolumeForMonth = (month: number) => {
        const monthDetail = _.find(props.orderVolume, (monthVolume: any) => {
            return monthVolume.month === month;
        });

        return {
            month_str: getMonthLabel(month),
            month,
            order_count: _.get(monthDetail, "order_count", 0),
        };
    };

    const _getShipmentVolumeForMonth = (month: number) => {
        const monthDetail = _.find(props.shipmentVolume, (monthVolume: any) => {
            return monthVolume.month === month;
        });

        return {
            month_str: getMonthLabel(month),
            month,
            shipment_count: _.get(monthDetail, "shipment_count", 0),
        };
    };

    const renderD3Chart = (animate: boolean = false) => {
        if (!chartRef.current || !props.orderVolume || !props.orderVolume.length) return;

        const svgElement = d3.select(chartRef.current);

        // Get the actual width of the container
        const containerWidth = chartRef.current.parentElement?.clientWidth || 600;
        const containerHeight = 275; // Fixed height

        // If container has no width, try again later
        if (containerWidth <= 0) {
            setTimeout(() => renderD3Chart(animate), 100);
            return;
        }

        // Set dimensions with extra bottom margin for legend
        const margin = { top: 30, right: 30, bottom: 60, left: 50 };
        const width = containerWidth - margin.left - margin.right;
        const height = containerHeight - margin.top - margin.bottom;

        // Transition settings
        const t = animate ? d3.transition().duration(300).ease(d3.easeCubicOut) : d3.transition().duration(0);

        // Set SVG dimensions with transition
        svgElement
            .transition(t as any)
            .attr("width", containerWidth)
            .attr("height", containerHeight);

        // Prepare the data
        const lastTwelveMonths = getLastTwelveMonths();

        const orderVolume = _.map(lastTwelveMonths, (monthDetail) => {
            return _getOrderVolumeForMonth(monthDetail.month);
        });

        const shipmentVolume = _.map(lastTwelveMonths, (monthDetail) => {
            return _getShipmentVolumeForMonth(monthDetail.month);
        });

        // Combine data for easier processing
        const chartData: ChartDataPoint[] = orderVolume.map((order, i) => ({
            month: order.month_str,
            month_num: order.month,
            order_count: order.order_count,
            shipment_count: shipmentVolume[i].shipment_count,
        }));

        // Get text color from CSS variable
        const textColor = getComputedStyle(document.documentElement).getPropertyValue("--main-text-color").trim() || "#333";

        // Set up scales
        const x0 = d3
            .scaleBand()
            .domain(chartData.map((d) => d.month))
            .range([0, width])
            .paddingInner(0.1);

        const x1 = d3.scaleBand().domain(["order_count", "shipment_count"]).range([0, x0.bandwidth()]).padding(0.05);

        // Find the maximum value for the y-axis
        const maxValue = d3.max(chartData, (d) => Math.max(d.order_count, d.shipment_count)) || 10;

        const y = d3
            .scaleLinear()
            .domain([0, maxValue * 1.1]) // Add 10% padding
            .nice()
            .range([height, 0]);

        // Define colors
        const colors = ["#f7931e", "#4a90e2"];

        // Check if chart needs to be initialized or updated
        if (!chartInitialized.current) {
            // Initial render - create all elements
            svgElement.selectAll("*").remove();

            const svg = svgElement.append("g").attr("class", "chart-root").attr("transform", `translate(${margin.left},${margin.top})`);

            // Add background rect
            svg.append("rect")
                .attr("class", "bg-rect")
                .attr("width", width)
                .attr("height", height)
                .attr("fill", "none")
                .attr("stroke", "#ddd");

            // Add the X axis
            svg.append("g")
                .attr("class", "x-axis")
                .attr("transform", `translate(0,${height})`)
                .call(d3.axisBottom(x0).tickSize(0))
                .selectAll("text")
                .attr("fill", textColor);

            // Add the Y axis
            svg.append("g")
                .attr("class", "y-axis")
                .call(d3.axisLeft(y).ticks(5).tickSize(-width))
                .call((g) => g.selectAll(".tick line").attr("stroke", "#ddd"))
                .selectAll("text")
                .attr("fill", textColor);

            // Create bars container
            const barsGroup = svg.append("g").attr("class", "bars-group");

            // Add the bars
            const groups = barsGroup
                .selectAll("g.month-group")
                .data(chartData, (d: any) => d.month)
                .enter()
                .append("g")
                .attr("class", "month-group")
                .attr("transform", (d) => `translate(${x0(d.month) || 0},0)`);

            // Order count bars
            groups
                .append("rect")
                .attr("class", "order-bar")
                .attr("x", x1("order_count") || 0)
                .attr("y", (d) => y(d.order_count))
                .attr("width", x1.bandwidth())
                .attr("height", (d) => height - y(d.order_count))
                .attr("fill", colors[0]);

            // Shipment count bars
            groups
                .append("rect")
                .attr("class", "shipment-bar")
                .attr("x", x1("shipment_count") || 0)
                .attr("y", (d) => y(d.shipment_count))
                .attr("width", x1.bandwidth())
                .attr("height", (d) => height - y(d.shipment_count))
                .attr("fill", colors[1]);

            // Add the legend
            const legendItems = [
                {
                    label: SupplierService.updateStringWithOrderNameOverride("Order Count", supplierModel, { uppercaseFirst: true }),
                    color: colors[0],
                },
                {
                    label: "Shipment Count",
                    color: colors[1],
                },
            ];

            const itemWidth = 120;
            const legendWidth = itemWidth * legendItems.length;

            const legend = svg
                .append("g")
                .attr("class", "legend")
                .attr("font-family", "sans-serif")
                .attr("font-size", 11)
                .attr("text-anchor", "start")
                .attr("transform", `translate(${(width - legendWidth) / 2}, ${height + 30})`);

            legendItems.forEach((item, i) => {
                const legendGroup = legend
                    .append("g")
                    .attr("class", "legend-item")
                    .attr("transform", `translate(${i * itemWidth}, 0)`);
                legendGroup.append("rect").attr("width", 16).attr("height", 16).attr("fill", item.color);
                legendGroup.append("text").attr("x", 24).attr("y", 8).attr("dy", "0.32em").attr("fill", textColor).text(item.label);
            });

            chartInitialized.current = true;
        } else {
            // Update existing chart with transitions
            const svg = svgElement.select("g.chart-root");

            // Update background rect
            svg.select("rect.bg-rect")
                .transition(t as any)
                .attr("width", width)
                .attr("height", height);

            // Update X axis
            svg.select("g.x-axis")
                .transition(t as any)
                .attr("transform", `translate(0,${height})`)
                .call(d3.axisBottom(x0).tickSize(0) as any)
                .selectAll("text")
                .attr("fill", textColor);

            // Update Y axis
            svg.select("g.y-axis")
                .transition(t as any)
                .call(d3.axisLeft(y).ticks(5).tickSize(-width) as any)
                .call((g) => g.selectAll(".tick line").attr("stroke", "#ddd"))
                .selectAll("text")
                .attr("fill", textColor);

            // Update bar groups
            const groups = svg
                .select("g.bars-group")
                .selectAll("g.month-group")
                .data(chartData, (d: any) => d.month);

            groups.transition(t as any).attr("transform", (d) => `translate(${x0(d.month) || 0},0)`);

            // Update order bars
            groups
                .select("rect.order-bar")
                .transition(t as any)
                .attr("x", x1("order_count") || 0)
                .attr("y", (d: any) => y(d.order_count))
                .attr("width", x1.bandwidth())
                .attr("height", (d: any) => height - y(d.order_count));

            // Update shipment bars
            groups
                .select("rect.shipment-bar")
                .transition(t as any)
                .attr("x", x1("shipment_count") || 0)
                .attr("y", (d: any) => y(d.shipment_count))
                .attr("width", x1.bandwidth())
                .attr("height", (d: any) => height - y(d.shipment_count));

            // Update legend position
            const itemWidth = 120;
            const legendWidth = itemWidth * 2;
            svg.select("g.legend")
                .transition(t as any)
                .attr("transform", `translate(${(width - legendWidth) / 2}, ${height + 30})`);
        }
    };

    // Render the chart when component mounts or when data changes
    useEffect(() => {
        if (props.orderVolume && props.orderVolume.length) {
            setHasData(true);
            // Reset chart initialization when data changes
            chartInitialized.current = false;
            // Use a small timeout to ensure the DOM is ready
            setTimeout(() => {
                renderD3Chart(false);
            }, 0);
        } else {
            setHasData(false);
            chartInitialized.current = false;
        }
    }, [props.orderVolume, props.shipmentVolume]);

    // Handle window resize
    useEffect(() => {
        const handleResize = _.debounce(() => {
            if (hasData) {
                renderD3Chart(true);
            }
        }, 100);

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [props.orderVolume, props.shipmentVolume, hasData]);

    // Handle container resize (for sidebar toggle)
    useEffect(() => {
        if (!containerRef.current) return;

        const handleContainerResize = _.debounce(() => {
            if (hasData) {
                renderD3Chart(true);
            }
        }, 50);

        const resizeObserver = new ResizeObserver(handleContainerResize);
        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
            handleContainerResize.cancel();
        };
    }, [hasData, props.orderVolume, props.shipmentVolume]);

    // Make sure the chart renders after the component is fully mounted
    useEffect(() => {
        if (chartRef.current && hasData) {
            // Small delay to ensure DOM is fully rendered
            const timer = setTimeout(() => {
                renderD3Chart(false);
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [chartRef.current]);

    return (
        <div className="YearlySnapshot h-100 w-100 d-flex flex-column mb-1">
            <div
                className={
                    "order-volume dashboard-widget " + (hasData ? "" : "d-flex justify-content-center align-items-center flex-column")
                }
                style={{ minHeight: props.inEditor ? "" : "245px", height: "100%" }}
                id={"order-volume"}>
                <div className={"ps-2 pe-2 d-flex justify-content-between"}>
                    <OwTitle title={SupplierService.updateStringWithOrderNameOverride("order_volume", supplierModel)} tag={"h4"} />
                    {props.inEditor ? (
                        <div className={""}>
                            <OwIconButton iconClass={"fas fa-trash-alt"} iconColorClass={"text-danger"} fn={props.removeWidget!} />
                        </div>
                    ) : null}
                </div>
                {!hasData ? (
                    <div className="d-flex justify-content-center align-items-center h-100">Not Enough Data</div>
                ) : (
                    <div ref={containerRef} className="chart-container" style={{ height: "100%", width: "100%", position: "relative" }}>
                        <svg
                            ref={chartRef}
                            style={{
                                width: "100%",
                                height: "295px" /* Increased height to accommodate legend */,
                                display: "block",
                            }}></svg>
                    </div>
                )}
            </div>
        </div>
    );
};
